"use client";

import React from "react";
import { User, UserStory } from "@/gql/graphql";
import useImage from "@/hooks/useImage";
import Image from "next/image";
import { usePathname, useRouter } from "next/navigation";
import { getProfileUrl } from "@/utils/getProfileUrl";

interface AvatarProps {
  image?: string;
  username?: string;
  size: number | string;
  story?: UserStory[] | string[];
  suggestion?: boolean;
  bold?: boolean;
  user?: User;
}

const Avatar = ({
  image,
  username,
  size,
  story,
  suggestion,
  bold,
  user,
}: AvatarProps) => {
  const router = useRouter();
  const pathname = usePathname();
  const { getUri } = useImage();

  const formattedSrc = getUri(image);
  const isProfilePath = pathname.startsWith("/profile");
  const numericSize =
    typeof size === "number" ? size : parseInt(String(size), 10) || 48;

  const goToStory = () => {
    if (story && story?.length > 0) {
      router.push(`/stories/${user?.id}`);
    }
  };

  const goToProfile = () => {
    if (!isProfilePath) {
      router.push(
        `${getProfileUrl(
          user?.Categories[0]?.name_en
            ? user?.Categories[0]?.name_en
            : "no-category",
          user?.name || "",
          String(user?.id)
        )}`
      );
    }
  };

  const handleClick = () => {
    if (story && story?.length > 0) {
      goToStory();
    } else {
      goToProfile();
    }
  };

  return (
    <div
      className="flex flex-col space-y-2 items-center justify-center cursor-pointer"
      onClick={handleClick}
    >
      <div
        className={`rounded-full flex items-center justify-center overflow-hidden ${
          story && story.length > 0
            ? "ring-offset-2 ring-2 ring-secondary"
            : "ring-1 ring-slate-200"
        }`}
        style={{
          width: `${numericSize}px`,
          height: `${numericSize}px`,
        }}
      >
        {image && formattedSrc ? (
          <div className="w-full h-full relative">
            <Image
              src={formattedSrc}
              width={numericSize}
              height={numericSize}
              quality={100}
              alt={`avatar-${username}`}
              priority
              className="object-cover w-full h-full"
              placeholder="empty"
              // Add onError fallback in case enhanced URL fails
              onError={(e) => {
                // Fallback to original URL if enhanced fails
                if (image && image !== formattedSrc) {
                  (e.target as HTMLImageElement).src = image;
                }
              }}
            />
          </div>
        ) : (
          <div
            className="w-full h-full relative flex items-center font-semibold bg-primary justify-center bg-cover"
            style={{ fontSize: Math.max(16, Math.round(numericSize * 0.45)) }}
          >
            {username?.charAt(0).toUpperCase()}
          </div>
        )}
      </div>
      {!suggestion && (
        <div
          className={`text-center w-5/6 text-sm line-clamp-3 truncate ${
            bold ? "font-bold" : ""
          }`}
        >
          {username}
        </div>
      )}
    </div>
  );
};

export default Avatar;
